import { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import ConceptMap from "./ConceptMap";
import { auth, signInWithGoogle, signOutUser } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";

const API_URL = "https://query-handler-869507078735.us-central1.run.app";

const SUGGESTIONS = [
  "What did Plato say about justice?",
  "How does Aristotle define happiness?",
  "What does Marcus Aurelius say about dealing with difficult people?",
  "What is the good life according to the philosophers?",
];

export default function App() {
  const [mode, setMode] = useState("chat");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Welcome to **Meletes** — a study companion for Greek philosophy. Ask me anything about Plato, Aristotle, or Marcus Aurelius and I'll answer from the original texts.",
      sources: [],
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  async function ask(question) {
    if (!question.trim()) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: question }]);
    setLoading(true);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ question }),
      });
      if (res.status === 429) {
        setMessages((prev) => [...prev, {
          role: "assistant",
          text: "You've reached your daily limit of questions. Come back tomorrow!",
          sources: []
        }]);
        return;
      }
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: data.answer, sources: data.sources || [] },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Something went wrong. Please try again.", sources: [] },
      ]);
    } finally {
      setLoading(false);
    }
  }

  if (authLoading) {
    return (
      <div style={styles.loadingScreen}>
        <div style={styles.loadingLogo}>MELETES</div>
        <div style={styles.loadingText}>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div style={styles.loginScreen}>
        <div style={styles.loginBox}>
          <div style={styles.loginOrnament}>✦ ✦ ✦</div>
          <h1 style={styles.loginLogo}>MELETES</h1>
          <p style={styles.loginTagline}>Greek Philosophy · Study & Practice</p>
          <div style={styles.loginDivider} />
          <p style={styles.loginQuote}>
            "The unexamined life is not worth living."
          </p>
          <p style={styles.loginQuoteAuthor}>— Socrates</p>
          <div style={styles.loginDivider} />
          <p style={styles.loginDesc}>
            Explore the wisdom of Plato, Aristotle, and Marcus Aurelius through original texts, powered by AI.
          </p>
          <button style={styles.googleBtn} onClick={signInWithGoogle}>
            <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: 10 }}>
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.app}>
      <header style={styles.header}>
        <h1 style={styles.logo}>MELETES</h1>
        <p style={styles.tagline}>Greek Philosophy · Study & Practice</p>
        <div style={styles.tabs}>
          <button
            style={{ ...styles.tab, ...(mode === "chat" ? styles.tabActive : {}) }}
            onClick={() => setMode("chat")}>
            Chat
          </button>
          <button
            style={{ ...styles.tab, ...(mode === "map" ? styles.tabActive : {}) }}
            onClick={() => setMode("map")}>
            Concept Map
          </button>
        </div>
        <div style={styles.userRow}>
          <span style={styles.userEmail}>{user.email}</span>
          <button style={styles.signOutBtn} onClick={signOutUser}>Sign out</button>
        </div>
      </header>

      {mode === "chat" ? (
        <>
          <div style={styles.chat}>
            {messages.map((msg, i) => (
              <div key={i} style={msg.role === "user" ? styles.userRow2 : styles.assistantRow}>
                <div style={msg.role === "user" ? styles.userBubble : styles.assistantBubble}>
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                  {msg.sources && msg.sources.length > 0 && (
                    <div style={styles.sources}>
                      {[...new Map(msg.sources.map((s) => [s.philosopher, s])).values()].map((s, j) => (
                        <span key={j} style={styles.sourceTag}>{s.philosopher}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div style={styles.assistantRow}>
                <div style={styles.assistantBubble}>
                  <span style={styles.thinking}>Searching the texts...</span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length === 1 && (
            <div style={styles.suggestions}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} style={styles.suggestion} onClick={() => ask(s)}>{s}</button>
              ))}
            </div>
          )}

          <div style={styles.inputRow}>
            <input
              style={styles.input}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask(input)}
              placeholder="Ask about Plato, Aristotle, or Marcus Aurelius..."
              disabled={loading}
            />
            <button
              style={styles.sendBtn}
              onClick={() => ask(input)}
              disabled={loading || !input.trim()}>
              Ask
            </button>
          </div>
        </>
      ) : (
        <div style={styles.mapWrap}>
          <ConceptMap user={user} />
        </div>
      )}
    </div>
  );
}

const styles = {
  app: { minHeight: "100vh", display: "flex", flexDirection: "column", background: "#0E0B18", fontFamily: "'EB Garamond', serif" },
  header: { background: "#0E0B18", padding: "1.25rem 2rem", textAlign: "center", borderBottom: "1px solid #7B5EA733" },
  logo: { fontFamily: "Cinzel, serif", color: "#C4A8F0", fontSize: "1.8rem", margin: 0, letterSpacing: "0.15em" },
  tagline: { color: "#5A5070", margin: "4px 0 0", fontSize: "0.75rem", letterSpacing: "0.18em", textTransform: "uppercase" },
  tabs: { display: "flex", gap: "0.5rem", justifyContent: "center", marginTop: "0.875rem" },
  tab: { fontFamily: "Georgia, serif", fontSize: "0.8rem", color: "#5A5070", background: "transparent", border: "0.5px solid #2D2640", borderRadius: 20, padding: "4px 20px", cursor: "pointer" },
  tabActive: { background: "#7B5EA720", color: "#C4A8F0", borderColor: "#7B5EA755" },
  userRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", marginTop: "0.5rem" },
  userEmail: { fontSize: "0.72rem", color: "#3A3050" },
  signOutBtn: { fontSize: "0.72rem", color: "#5A5070", background: "transparent", border: "0.5px solid #2D2640", borderRadius: 12, padding: "2px 10px", cursor: "pointer" },
  chat: { flex: 1, padding: "2rem", maxWidth: "780px", width: "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "1rem" },
  userRow2: { display: "flex", justifyContent: "flex-end" },
  assistantRow: { display: "flex", justifyContent: "flex-start" },
  userBubble: { background: "#7B5EA720", border: "0.5px solid #7B5EA755", color: "#E0D8F0", padding: "0.75rem 1.25rem", borderRadius: "12px 2px 12px 12px", maxWidth: "70%", fontSize: "0.95rem", lineHeight: 1.6 },
  assistantBubble: { background: "#130F20", border: "0.5px solid #2D2640", padding: "1rem 1.25rem", borderRadius: "2px 12px 12px 12px", maxWidth: "80%", fontSize: "0.95rem", lineHeight: 1.7, color: "#C0B8D8" },
  sources: { marginTop: "0.75rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" },
  sourceTag: { background: "#7B5EA710", color: "#C4A8F0", fontSize: "0.75rem", padding: "2px 10px", borderRadius: "12px", border: "0.5px solid #7B5EA755" },
  thinking: { color: "#3A3050", fontStyle: "italic", fontSize: "0.9rem" },
  suggestions: { display: "flex", flexWrap: "wrap", gap: "0.75rem", padding: "0 2rem 1rem", maxWidth: "780px", width: "100%", margin: "0 auto" },
  suggestion: { background: "#130F20", border: "0.5px solid #2D2640", borderRadius: "20px", padding: "0.5rem 1rem", fontSize: "0.82rem", cursor: "pointer", color: "#5A5070", fontFamily: "'EB Garamond', serif" },
  inputRow: { display: "flex", gap: "0.75rem", padding: "1rem 2rem 2rem", maxWidth: "780px", width: "100%", margin: "0 auto" },
  input: { flex: 1, padding: "0.75rem 1.25rem", borderRadius: "24px", border: "0.5px solid #2D2640", fontSize: "0.95rem", fontFamily: "'EB Garamond', serif", outline: "none", background: "#130F20", color: "#C0B8D8" },
  sendBtn: { background: "#7B5EA7", color: "#F0E8FF", border: "none", borderRadius: "24px", padding: "0.75rem 1.5rem", fontSize: "0.85rem", cursor: "pointer", fontFamily: "Cinzel, serif", letterSpacing: "0.05em" },
  mapWrap: { padding: "1.5rem", maxWidth: "900px", margin: "0 auto", width: "100%" },
  loadingScreen: { minHeight: "100vh", background: "#0E0B18", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "1rem" },
  loadingLogo: { fontFamily: "Cinzel, serif", fontSize: "2rem", color: "#C4A8F0", letterSpacing: "0.15em" },
  loadingText: { color: "#3A3050", fontFamily: "EB Garamond, serif", fontSize: "0.9rem" },
  loginScreen: { minHeight: "100vh", background: "#0E0B18", display: "flex", alignItems: "center", justifyContent: "center" },
  loginBox: { background: "#130F20", border: "1px solid #2D2640", borderRadius: 16, padding: "2.5rem", maxWidth: 380, width: "100%", textAlign: "center" },
  loginOrnament: { color: "#7B5EA733", fontSize: "1rem", letterSpacing: "0.6em", marginBottom: "1rem", fontFamily: "Cinzel, serif" },
  loginLogo: { fontFamily: "Cinzel, serif", color: "#C4A8F0", fontSize: "2rem", margin: "0 0 0.25rem", letterSpacing: "0.15em" },
  loginTagline: { color: "#5A5070", fontSize: "0.72rem", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: "1.25rem" },
  loginDivider: { height: "0.5px", background: "#2D2640", margin: "1rem 0" },
  loginQuote: { color: "#8070A8", fontStyle: "italic", fontSize: "0.95rem", lineHeight: 1.6 },
  loginQuoteAuthor: { color: "#3A3050", fontSize: "0.8rem", marginTop: "0.5rem", marginBottom: "1.25rem" },
  loginDesc: { color: "#5A5070", fontSize: "0.85rem", lineHeight: 1.6, marginBottom: "1.5rem" },
  googleBtn: { display: "flex", alignItems: "center", justifyContent: "center", width: "100%", padding: "0.75rem", background: "white", border: "none", borderRadius: 8, fontSize: "0.9rem", fontWeight: 500, cursor: "pointer", color: "#333" },
};